import api from './api';

/**
 * Service for handling email API requests
 */
class EmailService {
  /**
   * Send an email
   * @param {Object} emailData - Email data containing to, subject, and html
   * @returns {Promise<Object>} - Response from the API
   */
  async sendEmail(emailData) {
    try {
      console.log('Sending email with data:', {
        to: emailData.to,
        subject: emailData.subject,
        htmlLength: emailData.html ? emailData.html.length : 0
      });
      
      const response = await api.post('/email/send', emailData);
      return response.data;
    } catch (error) {
      console.error('Error sending email:', error);
      if (error.response) {
        console.error('Server response:', {
          status: error.response.status,
          data: error.response.data
        });
      }
      throw error;
    }
  }
  
  /**
   * Send tenant registration notification to admin
   * @param {Object} tenantData - Tenant data
   * @returns {Promise<Object>} - Response from the API
   */
  async sendTenantRegistrationNotification(tenantData) {
    try {
      console.log('Sending tenant registration notification for:', tenantData.name);
      
      const response = await api.post('/email/tenant-registration-notification', { tenantData });
      return response.data;
    } catch (error) {
      console.error('Error sending tenant registration notification:', error);
      if (error.response) {
        console.error('Server response:', {
          status: error.response.status,
          data: error.response.data
        });
      }
      // Don't throw the error - this shouldn't block the registration process
      return { success: false, error: error.message };
    }
  }
  
  /**
   * Send notification about tenant request status updates
   * @param {Object} notificationData - Contains recipient email, subject, and message
   * @returns {Promise<Object>} - Response from the API
   */
  async sendRequestStatusNotification(notificationData) {
    try {
      console.log('Sending status notification to:', notificationData.to);
      
      // Create structured email data
      const emailData = {
        to: notificationData.to,
        subject: notificationData.subject || 'Update on Your Organization Request',
        html: this.createStatusEmailTemplate(notificationData)
      };
      
      // Send the email using the general email endpoint
      const response = await this.sendEmail(emailData);
      console.log('Status notification sent:', response);
      return response;
    } catch (error) {
      console.error('Error sending status notification:', error);
      if (error.response) {
        console.error('Server response:', {
          status: error.response.status,
          data: error.response.data
        });
      }
      // Don't throw the error - this shouldn't block the main process
      return { success: false, error: error.message };
    }
  }
  
  /**
   * Creates a formatted HTML email template for status updates
   * @param {Object} data - Status update data
   * @returns {string} - HTML email content
   */
  createStatusEmailTemplate(data) {
    const { orgName, status, message, callToAction } = data;
    
    // Get appropriate status color and emoji
    let statusColor = '#3a7bd5'; // Default blue
    let statusEmoji = '📋';
    
    switch(status) {
      case 'approved':
        statusColor = '#4CAF50'; // Green
        statusEmoji = '✅';
        break;
      case 'rejected':
        statusColor = '#e53935'; // Red
        statusEmoji = '❌';
        break;
      case 'pending_info':
        statusColor = '#FF9800'; // Orange
        statusEmoji = '❓';
        break;
    }
    
    // Build the email template
    return `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <div style="background: linear-gradient(90deg, #3a7bd5, #00d2ff); padding: 20px; text-align: center; color: white;">
          <h1>Organization Request Update ${statusEmoji}</h1>
        </div>
        <div style="padding: 20px; border: 1px solid #e0e0e0; border-top: none;">
          <p>Hello,</p>
          <p>There has been an update to your organization request for <strong>${orgName}</strong>.</p>
          <div style="background-color: #f8f8f8; border-left: 4px solid ${statusColor}; padding: 15px; margin: 20px 0;">
            <p style="margin: 0; font-weight: bold;">Status: ${this.formatStatus(status)}</p>
            <p style="margin: 10px 0 0 0;">${message}</p>
          </div>
          ${callToAction ? `
          <div style="text-align: center; margin: 30px 0;">
            <a href="${callToAction.url}" 
              style="background: linear-gradient(90deg, ${statusColor}, ${this.lightenColor(statusColor, 20)}); color: white; padding: 12px 30px; text-decoration: none; border-radius: 5px; font-weight: bold;">
              ${callToAction.text}
            </a>
          </div>
          ` : ''}
          <hr style="border: none; border-top: 1px solid #e0e0e0; margin: 20px 0;">
          <p style="color: #666; font-size: 12px;">© ${new Date().getFullYear()} Transform Portal</p>
        </div>
      </div>
    `;
  }
  
  /**
   * Formats a status code into a readable string
   * @param {string} status - Status code
   * @returns {string} - Formatted status
   */
  formatStatus(status) {
    switch(status) {
      case 'approved':
        return 'Approved ✅';
      case 'rejected':
        return 'Not Approved ❌';
      case 'pending_info':
        return 'Additional Information Required ❓';
      case 'requested':
        return 'Under Review 📋';
      default:
        return status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ');
    }
  }
  
  /**
   * Lightens a color by the given percentage
   * @param {string} color - Hex color code
   * @param {number} percent - Percentage to lighten
   * @returns {string} - Lightened hex color
   */
  lightenColor(color, percent) {
    // Remove # if present
    color = color.replace('#', '');
    
    const num = parseInt(color, 16);
    const amt = Math.round(2.55 * percent);
    const R = (num >> 16) + amt;
    const G = (num >> 8 & 0x00FF) + amt;
    const B = (num & 0x0000FF) + amt;
    
    return '#' + (
      0x1000000 + 
      (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 + 
      (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 + 
      (B < 255 ? (B < 1 ? 0 : B) : 255)
    ).toString(16).slice(1);
  }
  
  /**
   * Send a password reset request
   * @param {string} email - User email
   * @returns {Promise<Object>} - Response from the API
   */
  async requestPasswordReset(email) {
    try {
      const response = await api.post('/email/forgot-password', { email });
      return response.data;
    } catch (error) {
      console.error('Error requesting password reset:', error);
      throw error;
    }
  }
  
  /**
   * Send financial request status notification to member or admin
   * @param {Object} notificationData - Contains recipient email, request details, and status
   * @returns {Promise<Object>} - Response from the API
   */
  async sendFinancialRequestNotification(notificationData) {
    try {
      console.log('Sending financial request notification to:', notificationData.to);
      
      // Create structured email data
      const emailData = {
        to: notificationData.to,
        subject: notificationData.subject || 'Update on Your Financial Assistance Request',
        html: this.createFinancialRequestEmailTemplate(notificationData)
      };
      
      // Send the email using the general email endpoint
      const response = await this.sendEmail(emailData);
      console.log('Financial request notification sent:', response);
      return response;
    } catch (error) {
      console.error('Error sending financial request notification:', error);
      if (error.response) {
        console.error('Server response:', {
          status: error.response.status,
          data: error.response.data
        });
      }
      // Don't throw the error - this shouldn't block the main process
      return { success: false, error: error.message };
    }
  }
  
  /**
   * Creates a formatted HTML email template for financial request status updates
   * @param {Object} data - Status update data
   * @returns {string} - HTML email content
   */
  createFinancialRequestEmailTemplate(data) {
    const { requestId, status, message, amount, reason, memberName, requestDate, recipientType } = data;
    
    // Get appropriate status color and icon based on status
    let statusColor = '#3a7bd5'; // Default blue
    let statusIcon = '📋';
    let statusText = 'Requested';
    
    switch(status) {
      case 'Requested':
        statusColor = '#2196F3'; // Blue
        statusIcon = '📋';
        statusText = 'Requested';
        break;
      case 'Review':
        statusColor = '#FF9800'; // Orange
        statusIcon = '🔍';
        statusText = 'In Review';
        break;
      case 'Approved':
        statusColor = '#4CAF50'; // Green
        statusIcon = '✅';
        statusText = 'Approved';
        break;
      case 'Declined':
        statusColor = '#F44336'; // Red
        statusIcon = '❌';
        statusText = 'Declined';
        break;
      case 'MemberRejected':
        statusColor = '#F44336'; // Red
        statusIcon = '⛔';
        statusText = 'Rejected by Member';
        break;
      case 'Disbursed':
        statusColor = '#9C27B0'; // Purple
        statusIcon = '💰';
        statusText = 'Funds Disbursed';
        break;
      case 'Completed':
        statusColor = '#4CAF50'; // Green
        statusIcon = '✓✓';
        statusText = 'Completed';
        break;
    }
    
    // Format date
    const formattedDate = new Date(requestDate).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
    
    // Build the email template
    const isAdmin = recipientType === 'admin';
    
    return `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <div style="background: linear-gradient(90deg, #3a7bd5, #00d2ff); padding: 20px; text-align: center; color: white;">
          <h1>Financial Assistance Request ${statusIcon}</h1>
        </div>
        <div style="padding: 20px; border: 1px solid #e0e0e0; border-top: none;">
          <p>Hello${isAdmin ? ' Admin' : (memberName ? ' ' + memberName : '')},</p>
          ${isAdmin 
            ? `<p>A new financial assistance request has been submitted and requires your attention.</p>` 
            : `<p>There has been an update to your financial assistance request.</p>`
          }
          
          <div style="background-color: #f8f8f8; border-left: 4px solid ${statusColor}; padding: 15px; margin: 20px 0;">
            <p style="margin: 0; font-weight: bold;">Status: ${statusText}</p>
            ${message ? `<p style="margin: 10px 0 0 0;">${message}</p>` : ''}
          </div>
          
          <div style="background-color: #f8f8f8; padding: 15px; margin: 20px 0; border-radius: 4px;">
            <h3 style="margin-top: 0; color: #333;">Request Details</h3>
            <p><strong>Request ID:</strong> ${requestId}</p>
            <p><strong>Date Submitted:</strong> ${formattedDate}</p>
            <p><strong>Reason:</strong> ${reason || 'Not specified'}</p>
            ${amount ? `<p><strong>Amount${status === 'Disbursed' ? ' Disbursed' : ' Requested'}:</strong> $${parseFloat(amount).toFixed(2)}</p>` : ''}
          </div>
          
          <div style="text-align: center; margin: 30px 0;">
            <a href="${isAdmin ? '/admin/financial-requests/' : '/member/financial-requests/'}${requestId}" 
              style="background: linear-gradient(90deg, ${statusColor}, ${this.lightenColor(statusColor, 20)}); color: white; padding: 12px 30px; text-decoration: none; border-radius: 5px; font-weight: bold;">
              View Request Details
            </a>
          </div>
          
          <hr style="border: none; border-top: 1px solid #e0e0e0; margin: 20px 0;">
          <p style="color: #666; font-size: 12px;">© ${new Date().getFullYear()} Transform Portal</p>
        </div>
      </div>
    `;
  }

  /**
   * Send notification to a member about account or payment updates
   * @param {Object} notificationData - Contains member email, notification type, and details
   * @returns {Promise<Object>} - Response from the API
   */
  async sendMemberNotification(notificationData) {
    try {
      console.log('Sending member notification to:', notificationData.to);
      
      // Create structured email data
      const emailData = {
        to: notificationData.to,
        subject: notificationData.subject || 'Update to Your Account',
        html: this.createMemberNotificationTemplate(notificationData)
      };
      
      // Send the email using the general email endpoint
      const response = await this.sendEmail(emailData);
      console.log('Member notification sent:', response);
      return response;
    } catch (error) {
      console.error('Error sending member notification:', error);
      if (error.response) {
        console.error('Server response:', {
          status: error.response.status,
          data: error.response.data
        });
      }
      // Don't throw the error - this shouldn't block the main process
      return { success: false, error: error.message };
    }
  }
  
  /**
   * Creates a formatted HTML email template for member notifications
   * @param {Object} data - Notification data
   * @returns {string} - HTML email content
   */
  createMemberNotificationTemplate(data) {
    const { 
      notificationType, 
      memberName, 
      organizationName, 
      details, 
      callToAction 
    } = data;
    
    // Get appropriate notification title, color and icon based on notification type
    let title = 'Account Update';
    let headerColor = 'linear-gradient(90deg, #3a7bd5, #00d2ff)'; // Default blue
    let icon = '📋';
    
    switch(notificationType) {
      case 'new_member':
        title = 'Welcome to Our Organization!';
        headerColor = 'linear-gradient(90deg, #43A047, #66BB6A)'; // Green
        icon = '🎉';
        break;
      case 'payment_recorded':
        title = 'Payment Recorded';
        headerColor = 'linear-gradient(90deg, #1E88E5, #42A5F5)'; // Blue
        icon = '💵';
        break;
      case 'payment_sent':
        title = 'Payment Sent to You';
        headerColor = 'linear-gradient(90deg, #7E57C2, #9575CD)'; // Purple
        icon = '💸';
        break;
      case 'debt_added':
        title = 'New Debt Added';
        headerColor = 'linear-gradient(90deg, #FB8C00, #FFA726)'; // Orange
        icon = '📝';
        break;
      case 'password_reset':
        title = 'Password Has Been Reset';
        headerColor = 'linear-gradient(90deg, #546E7A, #78909C)'; // Blue Grey
        icon = '🔑';
        break;
      case 'account_updated':
        title = 'Account Information Updated';
        headerColor = 'linear-gradient(90deg, #00ACC1, #26C6DA)'; // Cyan
        icon = '✏️';
        break;
      case 'payment_updated':
        title = 'Payment Information Updated';
        headerColor = 'linear-gradient(90deg, #1E88E5, #42A5F5)'; // Blue
        icon = '🔄';
        break;
    }
    
    // Build the email template
    return `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <div style="background: ${headerColor}; padding: 20px; text-align: center; color: white;">
          <h1>${title} ${icon}</h1>
        </div>
        <div style="padding: 20px; border: 1px solid #e0e0e0; border-top: none;">
          <p>Hello ${memberName || 'Member'},</p>
          <p>${details.message}</p>
          
          ${details.items && details.items.length > 0 ? `
          <div style="background-color: #f8f8f8; padding: 15px; margin: 20px 0; border-radius: 4px;">
            <h3 style="margin-top: 0; color: #333;">${details.itemsTitle || 'Details'}</h3>
            <ul style="padding-left: 20px;">
              ${details.items.map(item => `<li><strong>${item.label}:</strong> ${item.value}</li>`).join('')}
            </ul>
          </div>
          ` : ''}
          
          ${callToAction ? `
          <div style="text-align: center; margin: 30px 0;">
            <a href="${callToAction.url}" 
              style="background: ${headerColor}; color: white; padding: 12px 30px; text-decoration: none; border-radius: 5px; font-weight: bold;">
              ${callToAction.text}
            </a>
          </div>
          ` : ''}
          
          <hr style="border: none; border-top: 1px solid #e0e0e0; margin: 20px 0;">
          <p>Thank you,<br>${organizationName || 'The Team'}</p>
          <p style="color: #666; font-size: 12px;">© ${new Date().getFullYear()} ${organizationName || 'Transform Portal'}</p>
        </div>
      </div>
    `;
  }

  /**
   * Send financial report emails to selected members or all active members
   * @param {Object} reportData - Contains report type, recipient info, and email content
   * @returns {Promise<Object>} - Response from the API
   */
  async sendFinancialReportEmail(reportData) {
    try {
      console.log('Sending financial report emails with data:', reportData);
      
      const response = await api.post('/email/financial-report/email', reportData);
      return response.data;
    } catch (error) {
      console.error('Error sending financial report emails:', error);
      if (error.response) {
        console.error('Server response:', {
          status: error.response.status,
          data: error.response.data
        });
      }
      throw error;
    }
  }
}

export default new EmailService();
